import React, { useCallback, useState } from 'react'
import { Formik, Form } from 'formik'
import { useMutation } from 'react-apollo'
import { useHistory } from 'react-router'

import { PLACE_APPLE_PAY_ORDER, GET_CART_FOR_CHECKOUT } from '../../apollo/cart/cart.graphql'
import { dataByTypename } from '../../utils/apollo-helpers'
import { useGetCart } from '../CartQuery/CartQuery'
import Fieldset from '../NestedModifiers/Fieldset/Fieldset'
import CartTable from '../Cart/CartTable/CartTable'
import CartFooter from '../Cart/CartFooter/CartFooter'
import { useRestaurant } from '../RestaurantProvider/RestaurantProvider'
import { useApplePay, contactInfoSchema, useFormikValues } from './checkout-helpers'
import { makeApplePayPayment } from '../ApplePay/apple-pay-utils'
import { useFlag } from '../FeatureFlag/FeatureFlag'
import { useSavedAddresses } from '../use-saved-addresses/use-saved-addresses'
import Progress from '../Progress/Progress'
import * as CheckoutDeliveryInfo from '../CheckoutForm/CheckoutDeliveryInfo/CheckoutDeliveryInfo'
import * as Tip from '../CheckoutForm/Tip/Tip'
import * as CurbsidePickup from '../CheckoutForm/CurbsidePickup/CurbsidePickup'
import * as DeliveryCommunicationConsent from '../CheckoutForm/DeliveryCommunicationConsent/DeliveryCommunicationConsent'

import ErrorComponent from '../Error/Error'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import { useTrackPurchase, getFlagsLoading } from '../../utils/checkout-helpers'
import { useCurbsidePickup } from '../use-curbside-pickup/use-curbside-pickup'

import './CheckoutApplePay.css'
import { getRawPhoneNumber } from '../../utils/form-utils'

const formModules = [
  CheckoutDeliveryInfo,
  CurbsidePickup,
  DeliveryCommunicationConsent,
  Tip
]
export const CheckoutApplePay = () => {
  const featureFlags = {
    'oo-v3-include-default-tip': useFlag('oo-v3-include-default-tip', false),
    'oo-v3-delivery': useFlag('oo-v3-delivery', false),
    'oo-v3-curbside-pickup-v2': useFlag('oo-v3-curbside-pickup-v2', false)
  }
  const { curbsidePickupConfig, ...curbside } = useCurbsidePickup()
  const trackPurchase = useTrackPurchase()
  const [ errorMessage, setErrorMessage ] = useState()
  const fulfillmentContext = useFulfillment()
  const { savedAddresses } = useSavedAddresses()
  const { loading, error, getConfig, getMerchantSession } = useApplePay()
  const history = useHistory()
  const { getRestaurantPath } = useRestaurant()
  const [ checkout ] = useMutation(PLACE_APPLE_PAY_ORDER)
  const { cart, deleteCartGuid, loading: cartLoading, error: cartError } = useGetCart(GET_CART_FOR_CHECKOUT)
  const context = {
    cart,
    featureFlags,
    fulfillmentContext,
    savedAddresses,
    curbsidePickupConfig
  }

  const { initialValues, validationSchema } = useFormikValues({ context, formModules })

  const getPerformTransaction = useCallback((values, { setSubmitting }) => async (payment) => {
    const { shippingContact } = payment
    shippingContact.phoneNumber = getRawPhoneNumber(shippingContact.phoneNumber)

    try {
      await contactInfoSchema.validate(shippingContact)

      const initialContactInfo = {
        cartGuid: cart.guid,
        pkPaymentToken: JSON.stringify(payment),
        customer: {
          email: shippingContact.emailAddress,
          firstName: shippingContact.givenName,
          lastName: shippingContact.familyName,
          phone: shippingContact.phoneNumber
        }
      }

      const response = await checkout({
        variables: {
          input: formModules.reduce((acc, formModule) => {
            return {
              ...acc,
              ...formModule.getArgsForSubmit({ ...context, values })
            }
          }, initialContactInfo)
        }
      })

      const {
        PlaceOrderResponse,
        PlaceOrderError,
        PlaceOrderCartUpdatedError
      } = dataByTypename(response.data.placeApplePayOrder)
      const error = PlaceOrderError || PlaceOrderCartUpdatedError
      if (error) {
        throw new Error(error.message)
      } else {
        deleteCartGuid()
        const { guid } = PlaceOrderResponse.completedOrder
        trackPurchase(PlaceOrderResponse.completedOrder)
        history.replace(getRestaurantPath(`confirm/${guid}`))
      }
    } catch (error) {
      setErrorMessage(error)
      throw error
    } finally {
      setSubmitting(false)
    }
  }, [cart.guid, checkout, context, deleteCartGuid, getRestaurantPath, history, trackPurchase])

  const flagsLoading = getFlagsLoading(featureFlags)
  if (loading || flagsLoading || cartLoading || curbside.loading) return <Progress eventData={{ category: 'Checkout Apple Pay' }} />
  const displayError = error || cartError || curbside.error
  if (displayError) return <ErrorComponent error={displayError} />
  const { creditCardConfig = {} } = cart.restaurant
  const canShowTip = creditCardConfig.tipEnabled
  const showConsent = cart &&
  cart.deliveryProviderInfo &&
  cart.deliveryProviderInfo.needsDeliveryCommunicationConsent
  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        const { tipAmount } = Tip.getArgsForSubmit({values, cart})
        const config = getConfig(tipAmount)
        const performTransaction = getPerformTransaction(values, { setSubmitting })
        const onCancel = () => setSubmitting(false)
        makeApplePayPayment({ config, getMerchantSession, performTransaction, onCancel })
      }}>
      {({ values, isSubmitting }) => (
        <Form>
          <React.Fragment>
            <Fieldset
              label={`Your Order (${cart.order.numberOfSelections})`}
              collapsable
              collapsed
            >
              <CartTable cart={cart} />
            </Fieldset>
            <CurbsidePickup.Component curbsidePickupConfig={curbsidePickupConfig} />
            <CheckoutDeliveryInfo.Component savedAddresses={savedAddresses} />
            {showConsent && <div styleName='consent'><DeliveryCommunicationConsent.Component cart={cart} /></div>}
            {canShowTip && <Tip.Component />}
            <CartFooter
              showTip={canShowTip}
              tip={parseFloat(values.creditCardTip) || null}
              cart={cart} showTotal />
            {errorMessage && <ErrorComponent error={errorMessage} />}
            <button
              disabled={isSubmitting}
              type='submit'
              styleName='applePay'
              data-testid='apple-pay-checkout-button'
            />
          </React.Fragment>
        </Form>
      )}
    </Formik>
  )
}
